/** Subtle UI sounds, off by default (a per-device choice in the settings pill). */
export const SOUND_KEY = "uz.world.sound";

export function soundEnabled(): boolean {
  try {
    return globalThis.localStorage?.getItem(SOUND_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    globalThis.localStorage?.setItem(SOUND_KEY, on ? "1" : "0");
  } catch {
    // Blocked storage: the choice lasts for this visit only.
  }
}

let ctx: AudioContext | null = null;

/** A short soft blip: `up` rises (open, enter), `down` falls (close, exit). */
export function playUi(kind: "up" | "down" | "tick" = "tick"): void {
  if (!soundEnabled()) return;
  try {
    const Ctor =
      globalThis.AudioContext ??
      (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    ctx ??= new Ctor();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const [from, to] = kind === "up" ? [440, 660] : kind === "down" ? [600, 400] : [520, 520];
    osc.type = "sine";
    osc.frequency.setValueAtTime(from, now);
    osc.frequency.linearRampToValueAtTime(to, now + 0.09);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.05, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.14);
  } catch {
    // No audio device or blocked by autoplay policy: sounds are optional.
  }
}

/**
 * Elevator sounds, synthesised (nothing to download or license), off with the same toggle as the
 * other sounds: `ding` is the two-tone arrival chime, `door` a soft whoosh, `press` a button click.
 */
export function playElevator(kind: "ding" | "door" | "press"): void {
  if (!soundEnabled()) return;
  try {
    const Ctor =
      globalThis.AudioContext ??
      (globalThis as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    ctx ??= new Ctor();
    const now = ctx.currentTime;
    const tone = (freq: number, at: number, len: number, level: number) => {
      const osc = ctx!.createOscillator();
      const gain = ctx!.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, now + at);
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(level, now + at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + len);
      osc.connect(gain).connect(ctx!.destination);
      osc.start(now + at);
      osc.stop(now + at + len + 0.02);
    };
    if (kind === "ding") {
      tone(880, 0, 0.5, 0.06);
      tone(660, 0.22, 0.7, 0.05);
    } else if (kind === "press") {
      tone(1400, 0, 0.05, 0.03);
    } else {
      tone(180, 0, 0.35, 0.02);
    }
  } catch {
    // No audio device or blocked by autoplay policy: sounds are optional.
  }
}
